/**
 * Sync Validation Page
 * Per-table row-count reconciliation for an account: Mongo (entity only) vs StarRocks vs Warehouse.
 *
 * Tables are discovered from file_metadata (plus fixed entity tables) and split into:
 *   - Events  — all *_events tables (StarRocks + Warehouse)
 *   - Entity  — everything else (Mongo + StarRocks + Warehouse)
 *
 * Rows start unsynced (counts hidden). Clicking Sync on a row fetches its counts on demand —
 * nothing queries the engines automatically.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle, XCircle, RefreshCw, RotateCw, GitMerge, Database, ListChecks, DatabaseZap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingState, ErrorState } from '@/components/ui/loading-state'
import { cn, formatDate, formatNumber } from '@/lib/utils'
import { useAccount } from '@/contexts/AccountContext'
import { getTableRecon, syncTable, refreshAllRecon } from '../queries/syncValidation'
import type { TableReconResponse, TableReconRow } from '../types'

// ── Match status ─────────────────────────────────────────────────────────────

/** Reconciles the row's counts into one status. Entity uses Mongo as the reference. */
function matchStatus(row: TableReconRow): { ok: boolean; label: string } {
  if (!row.synced) return { ok: false, label: 'Not synced' }

  const isEntity = row.tableType === 'entity'
  const vals = isEntity
    ? [row.mongoCount, row.starrocksRowCount, row.warehouseRowCount]
    : [row.starrocksRowCount, row.warehouseRowCount]

  if (vals.some((v) => v < 0)) return { ok: false, label: 'Query failed' }
  if (vals.every((v) => v === vals[0])) return { ok: true, label: 'Matched' }

  if (isEntity) {
    const srMatch = row.starrocksRowCount === row.mongoCount
    const whMatch = row.warehouseRowCount === row.mongoCount
    if (!srMatch && !whMatch) return { ok: false, label: 'Both gap' }
    return { ok: false, label: srMatch ? 'Warehouse gap' : 'StarRocks gap' }
  }
  return { ok: false, label: 'SR / WH mismatch' }
}

// ── Cells ────────────────────────────────────────────────────────────────────

function CountCell({ value }: { value: number }) {
  return (
    <td className="py-2.5 px-3 text-right tabular-nums">
      {value < 0 ? <span className="text-gray-400">—</span> : formatNumber(value)}
    </td>
  )
}

function SyncButton({ synced, syncing, onClick }: { synced: boolean; syncing: boolean; onClick: () => void }) {
  return (
    <Button
      variant={synced ? 'outline' : 'default'}
      size="sm"
      className="h-7 px-2 text-xs gap-1"
      disabled={syncing}
      onClick={onClick}
    >
      <RotateCw className={cn('h-3 w-3', syncing && 'animate-spin')} />
      {synced ? 'Re-sync' : 'Sync'}
    </Button>
  )
}

// ── Table ────────────────────────────────────────────────────────────────────

function ReconTable({
  rows,
  showMongo,
  emptyLabel,
  onSync,
  syncingTable,
}: {
  rows: TableReconRow[]
  showMongo: boolean
  emptyLabel: string
  onSync: (table: string) => void
  syncingTable: string | null
}) {
  if (rows.length === 0) {
    return <p className="text-sm text-gray-400 italic text-center py-8">{emptyLabel}</p>
  }

  const colCount = showMongo ? 6 : 5

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-3 text-gray-500 font-medium">Table</th>
            {showMongo && <th className="text-right py-2 px-3 text-gray-500 font-medium">Mongo</th>}
            <th className="text-right py-2 px-3 text-gray-500 font-medium">StarRocks</th>
            <th className="text-right py-2 px-3 text-gray-500 font-medium">Warehouse</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Match</th>
            <th className="text-center py-2 px-3 text-gray-500 font-medium">Sync</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const match = matchStatus(row)
            return (
              <tr key={row.table} className={cn('border-b border-gray-100 last:border-0', row.synced && !match.ok && 'bg-red-50/40')}>
                <td className="py-2.5 px-3">
                  <span className="font-mono text-xs text-gray-700">{row.table}</span>
                  {row.lastCheckedAt && (
                    <div className="text-[10px] text-gray-400 mt-0.5">synced {formatDate(row.lastCheckedAt)}</div>
                  )}
                </td>
                {showMongo && <CountCell value={row.mongoCount} />}
                <CountCell value={row.starrocksRowCount} />
                <CountCell value={row.warehouseRowCount} />
                <td className="py-2.5 px-3 text-center">
                  <span className={cn(
                    'inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border whitespace-nowrap',
                    match.ok
                      ? 'text-green-700 bg-green-50 border-green-200'
                      : row.synced ? 'text-red-700 bg-red-50 border-red-200' : 'text-gray-500 bg-gray-50 border-gray-200',
                  )}>
                    {match.ok ? <CheckCircle className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
                    {match.label}
                  </span>
                </td>
                <td className="py-2.5 px-3 text-center">
                  <SyncButton
                    synced={row.synced}
                    syncing={syncingTable === row.table}
                    onClick={() => onSync(row.table)}
                  />
                </td>
              </tr>
            )
          })}
          <tr className="border-t border-gray-100">
            <td colSpan={colCount} className="py-1.5 px-3 text-[11px] text-gray-400">
              {rows.length} table{rows.length !== 1 ? 's' : ''}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

// ── Page ───────────────────────────────────────────────────────────────────────

export function SyncValidationPage() {
  const { selectedAccountId } = useAccount()
  const accId: number = selectedAccountId && selectedAccountId !== 'ALL'
    ? parseInt(selectedAccountId, 10)
    : 7943

  const queryClient = useQueryClient()
  const queryKey = ['table-recon', accId]

  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useQuery({
    queryKey,
    queryFn: () => getTableRecon(accId),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  })

  const syncMutation = useMutation({
    mutationFn: (table: string) => syncTable(accId, table),
    onSuccess: (updated) => {
      queryClient.setQueryData<TableReconResponse>(queryKey, (prev) =>
        prev
          ? {
              ...prev,
              eventsTables: prev.eventsTables.map((r) => (r.table === updated.table ? updated : r)),
              entityTables: prev.entityTables.map((r) => (r.table === updated.table ? updated : r)),
            }
          : prev,
      )
    },
  })

  const syncingTable = syncMutation.isPending ? (syncMutation.variables ?? null) : null

  // Triggers the full recon refresh (the nightly job) on demand. It runs in the background, so we
  // don't refetch immediately — the user clicks Refresh once it has had time to progress.
  const refreshAllMutation = useMutation({
    mutationFn: refreshAllRecon,
  })
  const refreshAllAck = refreshAllMutation.data

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <GitMerge className="h-5 w-5 text-indigo-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Sync Validation</h1>
            <p className="text-sm text-gray-500">
              Per-table row counts — Mongo (entity) vs StarRocks vs Warehouse — Account {accId}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {dataUpdatedAt > 0 && (
            <span className="text-xs text-gray-400 whitespace-nowrap">{formatDate(new Date(dataUpdatedAt).toISOString())}</span>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => refreshAllMutation.mutate()}
            disabled={refreshAllMutation.isPending}
            className="gap-2"
            title="Re-sync counts for every tracked table across all accounts (the nightly job). May take a while."
          >
            <DatabaseZap className={cn('h-4 w-4', refreshAllMutation.isPending && 'animate-spin')} />
            {refreshAllMutation.isPending ? 'Starting…' : 'Sync all now'}
          </Button>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching} className="gap-2">
            <RefreshCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Full-refresh trigger result / error */}
      {refreshAllAck && (
        <p className="text-xs text-gray-500">
          {refreshAllAck.status === 'started' ? (
            <>
              <span className="font-medium text-green-700">Full sync started</span> in the background — it may take a
              few minutes. Click <span className="font-medium">Refresh</span> to see counts update as it progresses.
            </>
          ) : (
            <span className="text-amber-600">{refreshAllAck.message}</span>
          )}
        </p>
      )}
      {refreshAllMutation.isError && (
        <p className="text-xs text-red-600">
          Couldn’t start full sync: {refreshAllMutation.error instanceof Error ? refreshAllMutation.error.message : 'Unknown error'}
        </p>
      )}

      {isLoading && <LoadingState message="Loading account tables..." />}
      {error && (
        <ErrorState
          message={error instanceof Error ? error.message : 'Failed to load sync validation'}
          onRetry={() => refetch()}
        />
      )}

      {data && (
        <>
          <p className="text-xs text-gray-400">
            Counts are fetched on demand — click <span className="font-medium">Sync</span> on a row to check it against
            StarRocks and the warehouse (and Mongo for entity tables). Nothing queries the engines automatically.
          </p>

          {/* Events tables */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <ListChecks className="h-4 w-4 text-indigo-500" />
              <div>
                <h2 className="font-semibold text-gray-900">Events Tables</h2>
                <p className="text-sm text-gray-500 mt-0.5">All <span className="font-mono">*_events</span> tables — StarRocks vs Warehouse</p>
              </div>
            </div>
            <div className="px-2 py-2">
              <ReconTable
                rows={data.eventsTables}
                showMongo={false}
                emptyLabel="No event tables found for this account."
                onSync={(t) => syncMutation.mutate(t)}
                syncingTable={syncingTable}
              />
            </div>
          </div>

          {/* Entity tables */}
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
              <Database className="h-4 w-4 text-indigo-500" />
              <div>
                <h2 className="font-semibold text-gray-900">Entity Tables</h2>
                <p className="text-sm text-gray-500 mt-0.5">All remaining tables — Mongo vs StarRocks vs Warehouse</p>
              </div>
            </div>
            <div className="px-2 py-2">
              <ReconTable
                rows={data.entityTables}
                showMongo
                emptyLabel="No entity tables found for this account."
                onSync={(t) => syncMutation.mutate(t)}
                syncingTable={syncingTable}
              />
            </div>
          </div>
        </>
      )}
    </div>
  )
}
